import React, { useEffect, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import * as API from '../../utility/api'
import moment from 'moment'

const ACTIONS = {
  SALES: 'sales',
  DEBT: 'debt'
}

const SALES_VIEWS = {
  EVENTS: 'events',
  SUMMARY: 'summary',
  MONTHS: 'months',
  SELLERS: 'sellers'
}

const SALES_VIEW_LABELS = {
  [SALES_VIEWS.EVENTS]: 'tapahtumittain',
  [SALES_VIEWS.SUMMARY]: 'yhteenveto',
  [SALES_VIEWS.MONTHS]: 'kuukausittain',
  [SALES_VIEWS.SELLERS]: 'myyjittäin'
}

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) {
    return 'ei aikaväliä'
  }

  if (startDate && endDate) {
    const start = moment(startDate)
    const end = moment(endDate)
    if (
      start.isValid() &&
      end.isValid() &&
      start.isSame(end, 'month') &&
      start.date() === 1 &&
      end.isSame(start.clone().endOf('month'), 'day')
    ) {
      return start.format('MMMM YYYY')
    }

    return `${start.format('D.M.YYYY')}–${end.format('D.M.YYYY')}`
  }

  if (startDate) {
    return `${moment(startDate).format('D.M.YYYY')} alkaen`
  }

  return `${moment(endDate).format('D.M.YYYY')} asti`
}

const organizerLabel = organizer => `${organizer.name} (${organizer.id})`

const eventLabel = event => `${event.name} (${event.id})`

const sellerLabel = seller =>
  `${seller.name}${seller.email ? ` (${seller.email})` : ''} #${seller.id}`

const toSelectOption = (item, label) => ({
  value: String(item.id),
  label,
  item
})

const ReportFilterSelect = ({
  inputId,
  noOptionsMessage,
  isMulti = false,
  ...props
}) => (
  <Select
    inputId={inputId}
    className="raports-form-select"
    classNamePrefix="raports-form-select"
    isClearable
    isSearchable
    isMulti={isMulti}
    closeMenuOnSelect={!isMulti}
    hideSelectedOptions={false}
    placeholder="Hae..."
    noOptionsMessage={noOptionsMessage || (() => 'Ei osumia')}
    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
    menuPosition="fixed"
    styles={{
      menuPortal: base => ({ ...base, zIndex: 40 })
    }}
    {...props}
  />
)

const Raports = ({
  startDate,
  endDate,
  selectedMonth,
  monthLabels = [],
  onSelectMonth,
  onChangeDate,
  onClearDates,
  setRaportData
}) => {
  const [organizers, setOrganizers] = useState([])
  const [events, setEvents] = useState([])
  const [organizerID, setOrganizerID] = useState('')
  const [eventIds, setEventIds] = useState([])
  const [action, setAction] = useState(ACTIONS.SALES)
  const [salesView, setSalesView] = useState(SALES_VIEWS.EVENTS)
  const [includeOrganizer, setIncludeOrganizer] = useState(false)
  const [timeTarget, setTimeTarget] = useState('sold_at')
  const [loading, setLoading] = useState(false)
  const [filtersError, setFiltersError] = useState(null)
  const [eventsError, setEventsError] = useState(null)
  const [sellerID, setSellerID] = useState('')
  const [sellerQuery, setSellerQuery] = useState('')
  const [sellers, setSellers] = useState([])
  const [selectedSeller, setSelectedSeller] = useState(null)
  const [sellerError, setSellerError] = useState(null)
  const [scopeError, setScopeError] = useState(null)
  const reportRequestId = useRef(0)
  const reportActive = useRef(false)
  const loadReportRef = useRef(() => {})

  useEffect(() => {
    let cancelled = false

    API.GET('admin/reports/filters')
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setOrganizers(data.organizers || [])
        setFiltersError(null)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error(error)
        setFiltersError('Järjestäjien lataus epäonnistui')
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setEvents([])
    setEventIds([])
    setEventsError(null)

    if (!organizerID) {
      return
    }

    let cancelled = false

    API.GET('admin/reports/filters', { organizer_id: organizerID })
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setEvents(data.events || [])
        setEventsError(null)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error(error)
        setEvents([])
        setEventIds([])
        setEventsError('Tapahtumien lataus epäonnistui')
      })

    return () => {
      cancelled = true
    }
  }, [organizerID])

  useEffect(() => {
    const query = sellerQuery.trim()
    const isId = /^[1-9]\d*$/.test(query)
    if (!isId && query.length < 2) {
      setSellers([])
      setSellerError(null)
      return
    }

    let cancelled = false
    const handle = setTimeout(() => {
      API.GET('admin/reports/filters', { seller_q: query })
        .then(({ data }) => {
          if (cancelled) {
            return
          }
          setSellers(data.sellers || [])
          setSellerError(null)
        })
        .catch(error => {
          if (cancelled) {
            return
          }
          console.error(error)
          setSellers([])
          setSellerError('Myyjien haku epäonnistui')
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [sellerQuery])

  const organizerOptions = useMemo(
    () =>
      organizers.map(organizer =>
        toSelectOption(organizer, organizerLabel(organizer))
      ),
    [organizers]
  )

  const eventOptions = useMemo(
    () => events.map(event => toSelectOption(event, eventLabel(event))),
    [events]
  )

  const sellerOptions = useMemo(() => {
    const options = selectedSeller
      ? [toSelectOption(selectedSeller, sellerLabel(selectedSeller))]
      : []
    for (const seller of sellers) {
      if (!options.some(option => option.value === String(seller.id))) {
        options.push(toSelectOption(seller, sellerLabel(seller)))
      }
    }
    return options
  }, [selectedSeller, sellers])

  const selectedOrganizerOption =
    organizerOptions.find(option => option.value === String(organizerID)) ||
    null
  const selectedEventOptions = eventOptions.filter(option =>
    eventIds.some(eventId => String(eventId) === option.value)
  )
  const selectedSellerOption =
    sellerOptions.find(option => option.value === String(sellerID)) || null

  const selectedOrganizer = organizers.find(
    organizer => String(organizer.id) === String(organizerID)
  )
  const isSales = action === ACTIONS.SALES
  const hasEntityFilter =
    Boolean(organizerID) ||
    eventIds.length > 0 ||
    (isSales && Boolean(sellerID))
  const hasDateRange = Boolean(startDate && endDate)
  const hasInvalidDateRange =
    hasDateRange && moment(startDate).isAfter(moment(endDate), 'day')

  const selectionSummary = useMemo(() => {
    const parts = [isSales ? 'Myynti' : 'Velka']

    if (isSales) {
      parts.push(SALES_VIEW_LABELS[salesView] || salesView)
    }

    parts.push(formatDateRange(startDate, endDate))

    if (selectedOrganizer) {
      parts.push(selectedOrganizer.name)
    } else {
      parts.push('kaikki järjestäjät')
    }

    if (eventIds.length === 1) {
      const event = events.find(
        item => String(item.id) === String(eventIds[0])
      )
      parts.push(event ? event.name : '1 tapahtuma')
    } else if (eventIds.length > 1) {
      parts.push(`${eventIds.length} tapahtumaa`)
    }

    if (isSales && selectedSeller) {
      parts.push(`myyjä ${selectedSeller.name} #${selectedSeller.id}`)
    }

    return parts.join(', ')
  }, [
    isSales,
    salesView,
    startDate,
    endDate,
    selectedOrganizer,
    eventIds,
    events,
    selectedSeller
  ])

  const loadReport = () => {
    if (hasInvalidDateRange) {
      setScopeError('Alkupäivä ei voi olla loppupäivän jälkeen.')
      return
    }

    if (!hasDateRange && !hasEntityFilter) {
      setScopeError(
        'Valitse aikaväli tai rajaa raportti järjestäjään, tapahtumaan tai myyjään.'
      )
      return
    }

    setScopeError(null)
    setLoading(true)
    setRaportData(null)
    reportActive.current = true
    const requestId = reportRequestId.current + 1
    reportRequestId.current = requestId
    const startTime = startDate
      ? moment(new Date(`${startDate} 00:00:00`))
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      : undefined
    const endTime = endDate
      ? moment(new Date(`${endDate} 23:59:59`))
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      : undefined

    API.POST(`admin/reports/${action}`, {
      start_time: startTime,
      end_time: endTime,
      organizer_id: organizerID || undefined,
      event_ids: eventIds,
      seller_id: isSales ? sellerID || undefined : undefined,
      time_target: timeTarget,
      view: isSales ? salesView : undefined,
      include_organizer: includeOrganizer
    })
      .then(({ data }) => {
        if (requestId !== reportRequestId.current) {
          return
        }
        setLoading(false)
        setRaportData(data)
      })
      .catch(error => {
        if (requestId !== reportRequestId.current) {
          return
        }
        setLoading(false)
        console.error(error)
        if (error?.response?.status === 400) {
          setScopeError(
            'Valitse aikaväli tai rajaa raportti järjestäjään, tapahtumaan tai myyjään.'
          )
        }
      })
  }

  loadReportRef.current = loadReport

  useEffect(() => {
    if (!reportActive.current) {
      return
    }

    loadReportRef.current()
  }, [salesView, includeOrganizer, action])

  const fetchData = event => {
    event.preventDefault()
    loadReport()
  }

  return (
    <form onSubmit={fetchData}>
      <div className="grid-2" style={{ alignItems: 'stretch' }}>
        <fieldset>
          <legend>Mitä</legend>
          <div className="u-col" style={{ gap: 18 }}>
            <div className="field">
              <label>Raportin tyyppi</label>
              <div className="seg">
                <button
                  type="button"
                  className={action === ACTIONS.SALES ? 'is-active' : ''}
                  onClick={() => setAction(ACTIONS.SALES)}
                >
                  Myynti
                </button>
                <button
                  type="button"
                  className={action === ACTIONS.DEBT ? 'is-active' : ''}
                  onClick={() => setAction(ACTIONS.DEBT)}
                >
                  Velka (sis. alv)
                </button>
              </div>
            </div>

            {isSales && (
              <>
                <div className="field">
                  <label>Näkymä</label>
                  <div className="chips">
                    <button
                      type="button"
                      className={`chip ${
                        salesView === SALES_VIEWS.EVENTS ? 'is-active' : ''
                      }`}
                      onClick={() => setSalesView(SALES_VIEWS.EVENTS)}
                    >
                      Tapahtumittain
                    </button>
                    <button
                      type="button"
                      className={`chip ${
                        salesView === SALES_VIEWS.SUMMARY ? 'is-active' : ''
                      }`}
                      onClick={() => setSalesView(SALES_VIEWS.SUMMARY)}
                    >
                      Vain yhteenveto
                    </button>
                    <button
                      type="button"
                      className={`chip ${
                        salesView === SALES_VIEWS.MONTHS ? 'is-active' : ''
                      }`}
                      onClick={() => setSalesView(SALES_VIEWS.MONTHS)}
                    >
                      Kuukausittain
                    </button>
                    <button
                      type="button"
                      className={`chip ${
                        salesView === SALES_VIEWS.SELLERS ? 'is-active' : ''
                      }`}
                      onClick={() => setSalesView(SALES_VIEWS.SELLERS)}
                    >
                      Myyjittäin
                    </button>
                  </div>
                </div>

                <div className="field field--inline">
                  <input
                    id="includeOrganizer"
                    type="checkbox"
                    checked={includeOrganizer}
                    onChange={e => setIncludeOrganizer(e.target.checked)}
                  />
                  <label
                    htmlFor="includeOrganizer"
                    style={{
                      textTransform: 'none',
                      letterSpacing: 0,
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'var(--lp-text)'
                    }}
                  >
                    Näytä järjestäjätason erittely
                  </label>
                </div>
              </>
            )}
          </div>
        </fieldset>

        <fieldset>
          <legend>Miltä ajalta ja keneltä</legend>
          <div className="u-col" style={{ gap: 18 }}>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="field">
                <label htmlFor="report-start-date">Alkaen</label>
                <input
                  id="report-start-date"
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={e => onChangeDate(e.target.value, 'startDate')}
                />
              </div>
              <div className="field">
                <label htmlFor="report-end-date">Päättyy</label>
                <input
                  id="report-end-date"
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={e => onChangeDate(e.target.value, 'endDate')}
                />
              </div>
            </div>

            <div className="field">
              <div className="spread" style={{ marginBottom: 6 }}>
                <label style={{ margin: 0 }}>Aikavälin pikavalinta</label>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={onClearDates}
                >
                  Tyhjennä aikaväli
                </button>
              </div>
              <div className="chips" style={{ gap: 6 }}>
                {monthLabels.map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    className={`chip btn--sm ${
                      selectedMonth === index ? 'is-active' : ''
                    }`}
                    onClick={() => onSelectMonth(index)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>Ajankohdan kohdistus</label>
              <div className="u-row" style={{ gap: 18 }}>
                <span className="u-row" style={{ gap: 9 }}>
                  <input
                    type="radio"
                    name="time-target"
                    id="sold_at"
                    checked={timeTarget === 'sold_at'}
                    onChange={() => setTimeTarget('sold_at')}
                  />
                  <label
                    htmlFor="sold_at"
                    style={{
                      textTransform: 'none',
                      letterSpacing: 0,
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'var(--lp-text)',
                      margin: 0
                    }}
                  >
                    Lipun myyntiaika
                  </label>
                </span>
                <span className="u-row" style={{ gap: 9 }}>
                  <input
                    type="radio"
                    name="time-target"
                    id="paytrail_payed_at"
                    checked={timeTarget === 'paytrail_payed_at'}
                    onChange={() => setTimeTarget('paytrail_payed_at')}
                  />
                  <label
                    htmlFor="paytrail_payed_at"
                    style={{
                      textTransform: 'none',
                      letterSpacing: 0,
                      fontSize: 14,
                      fontWeight: 400,
                      color: 'var(--lp-text)',
                      margin: 0
                    }}
                  >
                    Tilauksen maksuaika
                  </label>
                </span>
              </div>
            </div>

            <div className="grid-2" style={{ gap: 12 }}>
              <div className="field">
                <label htmlFor="organizerID">Järjestäjä</label>
                <ReportFilterSelect
                  inputId="organizerID"
                  options={organizerOptions}
                  value={selectedOrganizerOption}
                  placeholder="Kaikki järjestäjät"
                  onChange={option => {
                    setEvents([])
                    setEventIds([])
                    setEventsError(null)
                    setOrganizerID(option ? option.value : '')
                  }}
                />
                {filtersError && <p className="field__help">{filtersError}</p>}
              </div>
              <div className="field">
                <label htmlFor="eventIds">Tapahtumat</label>
                {organizerID ? (
                  <>
                    <ReportFilterSelect
                      inputId="eventIds"
                      isMulti
                      options={eventOptions}
                      value={selectedEventOptions}
                      placeholder="Kaikki tapahtumat"
                      noOptionsMessage={() => eventsError || 'Ei osumia'}
                      onChange={options =>
                        setEventIds(
                          (options || []).map(option => Number(option.value))
                        )
                      }
                    />
                    {eventsError && (
                      <p className="field__help">{eventsError}</p>
                    )}
                  </>
                ) : (
                  <p className="field__help">
                    Valitse järjestäjä, jos haluat rajata tapahtumiin.
                  </p>
                )}
              </div>
            </div>

            {isSales && (
              <div className="field">
                <label htmlFor="sellerID">Myyjä</label>
                <ReportFilterSelect
                  inputId="sellerID"
                  options={sellerOptions}
                  value={selectedSellerOption}
                  placeholder="Kaikki myyjät"
                  noOptionsMessage={() => {
                    const query = sellerQuery.trim()
                    if (sellerError) {
                      return sellerError
                    }
                    if (!query) {
                      return 'Kirjoita nimi, sähköposti tai ID'
                    }
                    if (!/^[1-9]\d*$/.test(query) && query.length < 2) {
                      return 'Kirjoita vähintään 2 merkkiä'
                    }
                    return 'Ei osumia'
                  }}
                  onInputChange={(value, { action: inputAction }) => {
                    if (inputAction === 'input-change') {
                      setSellerQuery(value)
                    }
                  }}
                  onChange={option => {
                    setSellerID(option ? option.value : '')
                    setSelectedSeller(option ? option.item : null)
                    setSellerQuery('')
                  }}
                />
                {sellerError && <p className="field__help">{sellerError}</p>}
              </div>
            )}
          </div>
        </fieldset>
      </div>

      <div
        className="spread"
        style={{
          background: 'var(--lp-grey-50)',
          borderRadius: 'var(--lp-r-md)',
          padding: '16px 20px',
          marginTop: 8
        }}
      >
        <div>
          <p className="eyebrow" style={{ margin: '0 0 3px' }}>
            Raportin rajaus
          </p>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--lp-ink)' }}>
            {selectionSummary}
            {timeTarget === 'sold_at'
              ? ' · lipun myyntiaika'
              : ' · tilauksen maksuaika'}
          </p>
          <p className="field__help">
            {hasEntityFilter
              ? 'Aikaväli on valinnainen, kun järjestäjä, tapahtuma tai myyjä on valittu.'
              : 'Ilman järjestäjä-, tapahtuma- tai myyjärajausta tarvitaan sekä alku- että loppupäivä.'}
          </p>
          {scopeError && (
            <p className="field__error" role="alert">
              {scopeError}
            </p>
          )}
        </div>
        <button
          className="btn btn--primary"
          disabled={loading}
          type="submit"
        >
          {loading ? 'Ladataan…' : 'Lataa raportti'}
        </button>
      </div>
    </form>
  )
}

export default Raports
